import { COGNITO_IDP_ENDPOINT } from '@volee/config'

import { AuthError } from './errors.ts'
import { authErrorCodeFor, type CognitoErrorOverrides } from './idp-errors.ts'

// The Cognito IDP JSON API, called by hand. Exactly two headers: they are the ones the
// endpoint's CORS preflight allows, which is why an AWS SDK cannot be used from a browser.
const HEADERS = { 'Content-Type': 'application/x-amz-json-1.1' }

interface IdpFailure {
  readonly __type?: string
  readonly message?: string
}

function failureOf(body: string): IdpFailure {
  try {
    return JSON.parse(body) as IdpFailure
  } catch {
    // A 5xx or a WAF block has no JSON body; letting SyntaxError escape would hide the status.
    return {}
  }
}

/** One POST to the IDP endpoint. Rejections become `AuthError`, never a raw response. */
export async function callIdp<TRequest, TResponse>(
  operation: string,
  payload: TRequest,
  overrides: CognitoErrorOverrides = {},
): Promise<TResponse> {
  let response: Response
  try {
    response = await fetch(COGNITO_IDP_ENDPOINT, {
      method: 'POST',
      headers: { ...HEADERS, 'X-Amz-Target': `AWSCognitoIdentityProviderService.${operation}` },
      body: JSON.stringify(payload),
    })
  } catch (cause) {
    throw new AuthError('idp_request_failed', `${operation} could not be reached`, { cause })
  }

  const body = await response.text().catch(() => '')

  if (!response.ok) {
    const failure = failureOf(body)
    throw new AuthError(
      authErrorCodeFor(failure.__type, overrides),
      `${operation} answered ${String(response.status)}: ${failure.__type ?? body}`,
    )
  }

  try {
    // ChangePassword and GlobalSignOut answer `{}`; an empty body is still a success.
    return (body === '' ? {} : JSON.parse(body)) as TResponse
  } catch (cause) {
    // A 200 that is not JSON — a captive portal, a proxy's error page — must not escape as a
    // bare SyntaxError: the caller branches on the code, and this file promises one.
    throw new AuthError(
      'idp_request_failed',
      `${operation} answered with a body that is not JSON`,
      {
        cause,
      },
    )
  }
}
